import { type FC, Suspense, lazy, useCallback, useState } from 'react';
import useLayout, { LayoutType } from 'utils/layout';

import styles from './DatePicker.module.scss';
import 'react-datepicker/dist/react-datepicker.css';
import DatePickerButton from './DatePickerButton';
import DatePickerHeader from './DatePickerHeader.tsx';
import './datepicker-custom-styles.scss';

type DatePickerProps = {
  title?: string;
  handleDateChange: (date: Date | null) => void;
  value: Date | null;
  maxDate?: Date;
  minDate?: Date;
};

const LazyDatePicker = lazy(() => import('react-datepicker'));

const DatePicker: FC<DatePickerProps> = ({
  handleDateChange,
  title,
  value,
  minDate,
  maxDate = new Date(),
}) => {
  const [isActive, setIsActive] = useState<boolean>(false);
  const layout = useLayout();

  const handleChangeDate = useCallback(
    (dateValue: Date | null): void => {
      handleDateChange(dateValue);
    },
    [handleDateChange],
  );

  const isDesktop = layout === LayoutType.wide;

  return (
    <div className={styles['datepicker-wrapper']}>
      {title && <span className={styles['datepicker-title']}>{title}</span>}
      <Suspense fallback={<div />}>
        <LazyDatePicker
          dateFormat="dd/MM/yyyy"
          maxDate={maxDate}
          minDate={minDate}
          selected={value}
          onChange={handleChangeDate}
          onCalendarClose={() => setIsActive(false)}
          onCalendarOpen={() => setIsActive(true)}
          customInput={<DatePickerButton isActive={isActive} />}
          calendarClassName="custom-datepicker"
          disabledKeyboardNavigation
          renderCustomHeader={DatePickerHeader}
          withPortal={!isDesktop}
        />
      </Suspense>
    </div>
  );
};

export default DatePicker;
